import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import {
  getFormError,
  getFormMeta,
  getFormSyncErrors,
  getFormValues,
  hasSubmitFailed,
} from 'redux-form';
import shortid from 'shortid';

import { Box, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';

import BackButtonComponent from './BackButtonComponent';
import FormFieldComponent from './FormFieldComponent';

import * as commonSelectors from '../Shared/selectors';

const StyledRoot = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isFullWidthForm' && prop !== 'isCentered',
})(({ theme, isFullWidthForm, isCentered }) => ({
  padding: '2rem 0',
  ...(isFullWidthForm
    ? { width: '100%' }
    : {
        [theme.breakpoints.up('xs')]: {
          width: '100%',
        },
        [theme.breakpoints.up('md')]: {
          width: '75%',
        },
        [theme.breakpoints.up('lg')]: {
          width: '50%',
        },
      }),
  ...(isCentered && { margin: '0 auto' }),
}));

const StyledErrorMessage = styled('div')(({ theme }) => ({
  paddingBottom: '1rem',
  color: theme.palette.secondary.main,
}));

const StyledDoubleField = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const GenericFormComponent = (props) => {
  const {
    isLoading,
    handleSubmit,
    isUpdate,
    title,
    canBeUpdated,
    formData,
    submitText,
    updateText,
    isCentered,
    customSubmitStyle,
    isFullWidthSubmit,
    isFullWidthForm,
    disabled,
    isHideSubmitButton,
    isButtonDisabled,
    isSkeletonDisabled,
    backUrl,
    backButton = true,
    CustomComponent,
    customSubmit, // add in the container
    formError,
    formSyncErrors,
    submitFailed,
    render,
    color,
  } = props;

  const submitId = useRef(shortid.generate());

  useEffect(() => {
    if (!title) return;
    document.title = title;
  }, []);

  useEffect(() => {
    // if there is a submit error scroll it into the view
    if (submitFailed && Object.values(formSyncErrors).length) {
      document
        .getElementsByClassName('MuiFormHelperText-root Mui-error')?.[0]
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
    }
  }, [submitFailed]);

  const onSubmit = handleSubmit((data) => customSubmit(data));

  if (render) return render({ ...props, onSubmit });

  return (
    <StyledRoot isCentered={isCentered} isFullWidthForm={isFullWidthForm}>
      <form onSubmit={onSubmit} method="post" autoComplete="off">
        {formError && <StyledErrorMessage>{formError}</StyledErrorMessage>}
        {formData.map((field) => {
          if (field.secondKeyName) {
            const { secondKeyName, secondLabel, ...rest } = field;
            return (
              <StyledDoubleField key={secondKeyName + secondLabel}>
                <FormFieldComponent
                  {...rest}
                  key={field.keyName}
                  disabled={disabled || !!(!canBeUpdated && isUpdate)}
                  isLoading={isLoading}
                  isSkeletonDisabled={isSkeletonDisabled}
                />
                <FormFieldComponent
                  {...rest}
                  key={secondKeyName}
                  keyName={secondKeyName}
                  label={secondLabel}
                  disabled={disabled || !!(!canBeUpdated && isUpdate)}
                  isLoading={isLoading}
                  isSkeletonDisabled={isSkeletonDisabled}
                />
              </StyledDoubleField>
            );
          }
          return (
            <FormFieldComponent
              key={field.keyName}
              validate={[field.validate]}
              isLoading={isLoading}
              isSkeletonDisabled={isSkeletonDisabled}
              {...field}
              disabled={
                field?.disabled || disabled || !!(!canBeUpdated && isUpdate)
              }
            />
          );
        })}
        <Grid
          container
          direction="row"
          alignItems="center"
          wrap="nowrap"
          gap="1rem"
        >
          {!isUpdate && !isHideSubmitButton ? (
            <Button
              type="submit"
              id={submitId.current}
              variant="contained"
              color={color || 'primary'}
              disableElevation
              disabled={isButtonDisabled || disabled || isLoading}
              fullWidth={isFullWidthSubmit}
              style={customSubmitStyle || {}}
            >
              {submitText}
            </Button>
          ) : null}
          {canBeUpdated && isUpdate ? (
            <Button
              type="submit"
              id={submitId.current}
              variant="contained"
              color="primary"
              disableElevation
              disabled={isButtonDisabled || disabled || isLoading}
              fullWidth={isFullWidthSubmit}
              style={customSubmitStyle || {}}
            >
              {updateText}
            </Button>
          ) : null}
          {backButton || CustomComponent ? (
            <Box display="flex">
              {backButton && <BackButtonComponent backUrl={backUrl} />}
              {CustomComponent && (
                <CustomComponent
                  disabled={isButtonDisabled || disabled || isLoading}
                  isLoading={isLoading}
                />
              )}
            </Box>
          ) : null}
        </Grid>
      </form>
    </StyledRoot>
  );
};

GenericFormComponent.defaultProps = {
  isFullWidthSubmit: false,
  isFullWidthForm: false,
  isButtonDisabled: false,
  isSkeletonDisabled: false,
  backUrl: '',
  backButton: true,
  CustomComponent: null,
  customSubmitStyle: {},
  isUpdate: false,
  isCentered: false,
  isHideSubmitButton: false,
  disabled: false,
  canBeUpdated: false,
  title: '',
  formError: '',
  submitText: 'Submit',
  updateText: 'Update',
  render: null,
  formSyncErrors: {},
};

GenericFormComponent.propTypes = {
  form: PropTypes.string.isRequired,
  formData: PropTypes.instanceOf(Array).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  customSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  customSubmitStyle: PropTypes.instanceOf(Object),
  isFullWidthSubmit: PropTypes.bool,
  isFullWidthForm: PropTypes.bool,
  isButtonDisabled: PropTypes.bool,
  isSkeletonDisabled: PropTypes.bool,
  backButton: PropTypes.bool,
  backUrl: PropTypes.string,
  isUpdate: PropTypes.bool,
  canBeUpdated: PropTypes.bool,
  title: PropTypes.string,
  submitText: PropTypes.string,
  updateText: PropTypes.string,
  isCentered: PropTypes.bool,
  disabled: PropTypes.bool,
  isHideSubmitButton: PropTypes.bool,
  CustomComponent: PropTypes.func,
  formError: PropTypes.string,
  render: PropTypes.func,
  formSyncErrors: PropTypes.instanceOf(Object),
  color: PropTypes.string,
};

const mapStateToProps = (state, { form }) => {
  const isLoading = commonSelectors.getIsLoading(state);
  const fieldsMeta = getFormMeta(form)(state);
  const isTouched = !!Object.values(fieldsMeta).find(
    ({ active, touched }) => active || touched,
  );
  const formError = getFormError(form)(state);
  const formValues = getFormValues(form)(state);
  const formSyncErrors = getFormSyncErrors(form)(state);
  const submitFailed = hasSubmitFailed(form)(state);
  return {
    isLoading,
    isTouched,
    formError,
    formValues,
    formSyncErrors,
    submitFailed,
  };
};

export default connect(mapStateToProps, null, null, {
  areMergedPropsEqual: (next, prev) => isEqual(next, prev),
})(GenericFormComponent);
